import {
  Box,
  Button,
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Divider,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getTransportClasses,
  updateTransportClass,
  createTransportClass,
  deleteTransportClass,
} from '../../services/transportClasses';
import {
  getTransportDistances,
  createTransportDistance,
  updateTransportDistances,
  deleteTransportDistance,
} from '../../services/transportDistances';
import {
  getTransportPrices,
  updateTransportPrice,
} from '../../services/transportPrices';
import { useState } from 'react';
import { TransportColumns } from '../../components/AgGrid/Columns/TransportColumns';
import { Edit, LocalShipping, Signpost } from '@mui/icons-material';
import {
  TransportClass,
  TransportDistance,
  TransportPrice,
} from '../../../../shared/types/transport';
import {
  TRANSPORT_CLASSES_QUERY_KEY,
  TRANSPORT_DISTANCES_QUERY_KEY,
  TRANSPORT_PRICES_QUERY_KEY,
} from '../../../../shared/constants';

const TransportPricing = () => {
  const [pricingId, setPricingId] = useState<number | undefined>(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [minDistance, setMinDistance] = useState('');
  const [maxDistance, setMaxDistance] = useState('');
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDistance, setSelectedDistance] =
    useState<TransportDistance | null>(null);
  const [editMinDistance, setEditMinDistance] = useState('');
  const [editMaxDistance, setEditMaxDistance] = useState('');

  const transportClassesQuery = useQuery({
    queryKey: [TRANSPORT_CLASSES_QUERY_KEY, pricingId],
    queryFn: () => getTransportClasses(pricingId),
    enabled: !!pricingId,
  });

  const transportDistancesQuery = useQuery({
    queryKey: [TRANSPORT_DISTANCES_QUERY_KEY, pricingId],
    queryFn: () => getTransportDistances(pricingId),
    enabled: !!pricingId,
  });

  const transportPricesQuery = useQuery({
    queryKey: [TRANSPORT_PRICES_QUERY_KEY, pricingId],
    queryFn: async () => {
      // Fetch latest class/distance data from qyeryClient cache
      const classes = queryClient.getQueryData<TransportClass[]>([
        TRANSPORT_CLASSES_QUERY_KEY,
        pricingId,
      ]);
      const distances = queryClient.getQueryData<TransportDistance[]>(
        [TRANSPORT_DISTANCES_QUERY_KEY, pricingId],
      );

      if (!classes?.length || !distances?.length) return [];

      return getTransportPrices(
        classes.map((c) => c.id),
        distances.map((d) => d.id),
      );
    },
    enabled:
      !!pricingId &&
      queryClient.getQueryData([
        TRANSPORT_CLASSES_QUERY_KEY,
        pricingId,
      ]) != null &&
      queryClient.getQueryData([
        TRANSPORT_DISTANCES_QUERY_KEY,
        pricingId,
      ]) != null,
  });

  const createDistanceMutation = useMutation({
    mutationFn: async () =>
      createTransportDistance({
        pricingId,
        distanceMin: Number(minDistance),
        distanceMax: Number(maxDistance),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TRANSPORT_DISTANCES_QUERY_KEY, pricingId],
      });
      setOpenDialog(false);
      setMinDistance('');
      setMaxDistance('');
    },
  });

  const updateDistancesMutation = useMutation({
    mutationFn: async (
      updatedDistances: {
        id: number;
        distanceMin: number;
        distanceMax: number;
      }[],
    ) => {
      return updateTransportDistances(updatedDistances);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TRANSPORT_DISTANCES_QUERY_KEY, pricingId],
      });
      setEditDialogOpen(false);
    },
  });

  const deleteDistanceMutation = useMutation({
    mutationFn: async (id: number) => {
      return deleteTransportDistance(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TRANSPORT_DISTANCES_QUERY_KEY, pricingId],
      });
      setEditDialogOpen(false);
    },
  });

  const updatePriceMutation = useMutation({
    mutationFn: async (updatedPrice: {
      transportClassId: number;
      transportDistanceId: number;
      price: number;
    }) => {
      return updateTransportPrice(updatedPrice);
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [TRANSPORT_PRICES_QUERY_KEY, pricingId],
      });
    },
  });

  const createTransportClassMutation = useMutation({
    mutationFn: createTransportClass,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TRANSPORT_CLASSES_QUERY_KEY, pricingId],
      });
    },
  });

  const updateTransportClassMutation = useMutation({
    mutationFn: updateTransportClass,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TRANSPORT_CLASSES_QUERY_KEY, pricingId],
      });
    },
  });

  const deleteTransportClassMutation = useMutation({
    mutationFn: async (id: number) => deleteTransportClass(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TRANSPORT_CLASSES_QUERY_KEY, pricingId],
      });
    },
  });

  const handleDeleteRow = (id: number) => {
    deleteTransportClassMutation.mutate(id);
  };

  const columnDefs = TransportColumns(
    transportDistancesQuery.data || [],
    handleDeleteRow,
  );

  if (
    transportClassesQuery.isLoading ||
    transportDistancesQuery.isLoading ||
    transportPricesQuery.isLoading
  ) {
    return <div>Ladataan...</div>;
  }

  const handleCellEdit = (params: any) => {
    const { data, colDef, newValue, oldValue } = params;

    if (oldValue === newValue) return;

    const transportClass = transportClassesQuery.data?.find(
      (c: any) => c.id === data.id,
    );

    if (
      transportClass &&
      [
        'class',
        'upToWeight',
        'expressDeliveryFee',
        'adrFee',
      ].includes(colDef.field)
    ) {
      updateTransportClassMutation.mutate({
        id: transportClass.id,
        transportPricingId: pricingId ?? 1,
        label:
          colDef.field === 'class' ? newValue : transportClass.label,
        upToWeight:
          colDef.field === 'upToWeight'
            ? Number(newValue) || 0
            : transportClass.upToWeight,
        expressDeliveryFee:
          colDef.field === 'expressDeliveryFee'
            ? Number(newValue) || 0
            : transportClass.expressDeliveryFee,
        adrFee:
          colDef.field === 'adrFee'
            ? Number(newValue) || 0
            : transportClass.adrFee,
      });
    }

    const transportClassId = transportClass?.id;
    const [distanceMin, distanceMax] = colDef.field
      .split('-')
      .map(Number);

    const transportDistanceId = transportDistancesQuery.data?.find(
      (d: any) =>
        d.distanceMin === distanceMin &&
        d.distanceMax === distanceMax,
    )?.id;

    if (transportClassId && transportDistanceId) {
      updatePriceMutation.mutate({
        transportClassId,
        transportDistanceId,
        price: Number(newValue) || 0,
      });
    }
  };

  const handleAddNewClass = () => {
    const weights =
      transportClassesQuery.data?.map((c: any) => c.upToWeight) ?? [];

    const maxUpToWeight =
      weights.length > 0 ? Math.max(...weights) : 0;

    createTransportClassMutation.mutate({
      transportPricingId: pricingId ?? 1,
      label: `Rahtiluokka ${transportClassesQuery.data?.length + 1}`,
      upToWeight: maxUpToWeight + 10000,
      expressDeliveryFee: 0,
      adrFee: 0,
    });
  };

  const rowData =
    transportClassesQuery.data?.map(
      (transportClass: TransportClass) => {
        const row: Record<string, any> = {
          id: transportClass.id,
          class: transportClass.label,
          upToWeight: transportClass.upToWeight,
          expressDeliveryFee: transportClass.expressDeliveryFee,
          adrFee: transportClass.adrFee,
        };
        transportDistancesQuery.data?.forEach(
          (distance: TransportDistance) => {
            const matchingPrice = transportPricesQuery.data?.find(
              (price: TransportPrice) =>
                price.transportClassId === transportClass.id &&
                price.transportDistanceId === distance.id,
            );

            const key = `${distance.distanceMin}-${distance.distanceMax}`;
            row[key] = matchingPrice
              ? `${matchingPrice.price}`
              : null;
          },
        );

        return row;
      },
    ) ?? [];

  return (
    <Container maxWidth={false} sx={{ marginBottom: '5rem' }}>
      <Box sx={{ marginBottom: '1rem' }}>
        <Typography
          variant={'h3'}
          color={'primary'}
          sx={{ marginBottom: '1rem' }}
        >
          Kuljetushinnastot
        </Typography>
        <Button
          sx={{ marginRight: '1rem' }}
          variant={pricingId === 1 ? 'contained' : 'outlined'}
          onClick={() => setPricingId(1)}
        >
          PK Hinnasto
        </Button>
        <Button
          variant={pricingId === 2 ? 'contained' : 'outlined'}
          onClick={() => setPricingId(2)}
        >
          Muu Suomi Hinnasto
        </Button>
      </Box>
      <Divider sx={{ marginY: '2rem' }} />
      <Box sx={{ marginBottom: '1rem' }}>
        <Typography
          variant={'h5'}
          color={'primary'}
          sx={{ marginBottom: '1rem' }}
        >
          {`${pricingId === 1 ? 'PK' : 'Muu Suomi'} hinnasto`}
        </Typography>
        <Button
          sx={{ height: 26 }}
          size="small"
          variant="outlined"
          color="primary"
          onClick={handleAddNewClass}
        >
          <LocalShipping
            style={{ fontSize: 16, marginRight: '0.5rem' }}
          />
          Lisää uusi rahtiluokka
        </Button>
        <Button
          sx={{ height: 26, marginLeft: '1rem' }}
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => setOpenDialog(true)}
        >
          <Signpost style={{ fontSize: 16, marginRight: '0.5rem' }} />
          Lisää uusi etäisyys
        </Button>
        <Button
          sx={{ height: 26, marginLeft: '1rem' }}
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => {
            if (transportDistancesQuery.data?.length) {
              setSelectedDistance(transportDistancesQuery.data[0]);
              setEditMinDistance(
                transportDistancesQuery.data[0].distanceMin.toString(),
              );
              setEditMaxDistance(
                transportDistancesQuery.data[0].distanceMax.toString(),
              );
              setEditDialogOpen(true);
            }
          }}
        >
          <Edit style={{ fontSize: 16, marginRight: '0.5rem' }} />
          Muokkaa etäisyyksiä
        </Button>
      </Box>

      <Box
        className="ag-theme-material custom-no-hamburger bordered-grid"
        sx={{
          marginTop: '2rem',
        }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          rowSelection="multiple"
          onCellValueChanged={handleCellEdit}
          domLayout="autoHeight"
          suppressHorizontalScroll={true}
          suppressMovableColumns={true}
        />
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Lisää uusi etäisyys</DialogTitle>
        <DialogContent>
          <TextField
            label="Min etäisyys (km)"
            type="number"
            value={minDistance}
            onChange={(e) => setMinDistance(e.target.value)}
            fullWidth
            sx={{ marginBottom: '1rem' }}
          />
          <TextField
            label="Max etäisyys (km)"
            type="number"
            value={maxDistance}
            onChange={(e) => setMaxDistance(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            Peruuta
          </Button>
          <Button
            onClick={() => createDistanceMutation.mutate()}
            color="primary"
            variant="contained"
            disabled={!minDistance || !maxDistance}
          >
            Lisää
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
      >
        <DialogTitle>Muokkaa etäisyyttä</DialogTitle>
        <DialogContent>
          <Typography sx={{ marginTop: '1rem' }}>
            Valitse muokattava etäisyys sarake:
          </Typography>
          {transportDistancesQuery.data?.map((distance: any) => (
            <Button
              sx={{
                margin: '1rem 1rem 2rem 0',
              }}
              key={distance.id}
              variant={
                selectedDistance?.id === distance.id
                  ? 'contained'
                  : 'outlined'
              }
              onClick={() => {
                setSelectedDistance(distance);
                setEditMinDistance(distance.distanceMin.toString());
                setEditMaxDistance(distance.distanceMax.toString());
              }}
            >
              {distance.distanceMin} - {distance.distanceMax} km
            </Button>
          ))}
          <TextField
            label="Min etäisyys (km)"
            type="number"
            value={editMinDistance}
            onChange={(e) => setEditMinDistance(e.target.value)}
            fullWidth
            sx={{ marginBottom: '1rem' }}
          />
          <TextField
            label="Max etäisyys (km)"
            type="number"
            value={editMaxDistance}
            onChange={(e) => setEditMaxDistance(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Peruuta
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!selectedDistance}
            onClick={() => {
              if (selectedDistance) {
                if (
                  window.confirm(
                    `Haluatko varmasti poistaa etäisyyden ${selectedDistance.distanceMin}-${selectedDistance.distanceMax} km?`,
                  )
                ) {
                  deleteDistanceMutation.mutate(selectedDistance.id);
                }
              }
            }}
          >
            Poista
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!editMinDistance || !editMaxDistance}
            onClick={() => {
              if (selectedDistance) {
                updateDistancesMutation.mutate([
                  {
                    id: selectedDistance.id,
                    distanceMin: Number(editMinDistance),
                    distanceMax: Number(editMaxDistance),
                  },
                ]);
              }
            }}
          >
            Päivitä
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TransportPricing;
